// Onboarding questions — a linked chain of question objects, each pointing to
// the next via next_q. The flow starts at FIRST_QUESTION.

import { ASSET_CLASSES, LIABILITY_CLASSES } from 'utilities/global.variables'

// Country options for the non-UK side quest (UK excluded — it has its own path).
export const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia',
  'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad',
  'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czechia',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
  'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia',
  'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands',
  'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia',
  'Zimbabwe',
]



// ─── Forecasting & life goals ───

// Final breather — terminal for now, the results screen comes later.
export const q_thanks_final = {
  id: 'q_thanks_final',
  section: 'Thank you',
  step: 24,
  question: 'Thank you for getting this far!\nWould you like to see your results?',
  button: 'See my results',
  next_q: null,
}

export const q_dob = {
  id: 'q_dob',
  section: 'Life goals',
  step: 23,
  question: 'Final question.\nWhat\'s your date of birth?',
  note: 'This helps forecast your life goals.',
  input: { type: 'date' },
  onboarding_key: 'date_of_birth',
  next_q: q_thanks_final,
}

export const q_retirement_amount = {
  id: 'q_retirement_amount',
  section: 'Life goals',
  step: 22,
  question: 'And how much you’d like to retire on?',
  input: { type: 'currency', placeholder: '£500,000' },
  onboarding_key: 'retirement_amount',
  next_q: q_dob,
}

export const q_retirement_age = {
  id: 'q_retirement_age',
  section: 'Life goals',
  step: 21,
  question: 'In this final section, think about your financial life goals.\n\nDo you have an idea of when you want to retire?',
    note: 'The current UK pension age is 67.',

  input: { type: 'number', placeholder: '60', maxLength: 2 },
  onboarding_key: 'retirement_age',
  next_q: q_retirement_amount,
}

// Liability rates and terms — sits in the forecasting section; only shown when
// at least one liability is being actively repaid.
export const q_liability_details = {
  id: 'q_liability_details',
  section: 'Forecasting',
  step: 20,
  question: 'Almost there! Add the interest rate and term to your liabilities.',
  note: 'Not sure? Put down an esimate. You can tweak your answers later.',
  input: {
    type: 'details-group',
    source_q: () => q_liabilities,
    options: (a) => Object.entries(a.q_liability_repaid || {})
      .filter(([, repaid]) => repaid)
      .map(([label]) => label)
      .sort((x, y) => {
        const classOf = (label) => q_liabilities.input.options.find(o => o.label === label)?.class
        return LIABILITY_CLASSES.indexOf(classOf(x)) - LIABILITY_CLASSES.indexOf(classOf(y))
      })
  },
  next_q: q_retirement_age
}

// Breather before the liability forecasting questions.
export const q_liability_forecast_intro = {
  id: 'q_liability_forecast_intro',
  section: 'Forecasting',
  step: 19,
  question: 'Nice work.\nNext, let’s do the same for your liabilities.',
  note: 'PocketWatch uses interest rates and terms to forecast your repayments.',
  next_q: q_liability_details,
}

export const q_asset_forecast = {
  id: 'q_asset_forecast',
  section: 'Forecasting',
  step: 18,
  question: 'How are your assets growing?',
  note: 'Add your annual contributions and expected growth rates. Not sure? Put down an estimate.',
  input: {
    type: 'forecast-group',
    source_q: () => q_assets,
    options: (a) => [...(a.q_assets || [])].sort((x, y) => {
      const classOf = (label) => q_assets.input.options.find(o => o.label === label)?.class
      return ASSET_CLASSES.indexOf(classOf(x)) - ASSET_CLASSES.indexOf(classOf(y))
    })
  },
  next_q: (value, a) => Object.values(a.q_liability_repaid || {}).some(Boolean) ? q_liability_forecast_intro : q_retirement_age,
}

// Breather explaining how asset forecasting works.
export const q_asset_growth_explainer = {
  id: 'q_asset_growth_explainer',
  section: 'Pocketwatch tip',
  step: 17,
  question: 'Asset values increase in two ways —\nwhat you add in, and how much they grow.',
    note: 'PocketWatch uses your annual contributions and growth estimates to forecast their future value.',

  next_q: q_asset_forecast,
}

// Breather before the forecasting questions.
export const q_forecast_intro = {
  id: 'q_forecast_intro',
  section: 'Forecasting and Goals',
  step: 16,
  question: 'You’re doing great!\nThis last section is about forecasting and goals.',
  next_q: q_asset_growth_explainer,
}

// ─── Liabilities ───

// Mortgage side quest — only shown when a mortgage was selected.
export const q_mortgage_type = {
  id: 'q_mortgage_type',
  section: 'Your Portfolio',
  step: 15,
  question: 'Finally, is your mortgage Repayment or Interest only?',
  input: { type: 'radio', options: ['Repayment', 'Interest only'] },
  onboarding_key: 'mortgage_type',
  next_q: q_forecast_intro,
}

// Where the liabilities section exits to: the mortgage side quest if one was selected.
const afterLiabilities = (a) => (a.q_liabilities || []).includes('Mortgage') ? q_mortgage_type : q_forecast_intro

export const q_liability_repaid = {
  id: 'q_liability_repaid',
  section: 'Your Portfolio',
  step: 14,
  question: 'Are you making repayments?',
  input: {
    type: 'toggle-group',
    source_q: () => q_liabilities,
    options: (a) => [...(a.q_liabilities || [])].sort((x, y) => {
      const classOf = (label) => q_liabilities.input.options.find(o => o.label === label)?.class
      return LIABILITY_CLASSES.indexOf(classOf(x)) - LIABILITY_CLASSES.indexOf(classOf(y))
    })
  },
  next_q: (value, a) => afterLiabilities(a)
}

export const q_liability_valuations = {
  id: 'q_liability_valuations',
  section: 'Your liabilities',
  step: 13,
  question: 'Let’s put some numbers in.',
  input: {
    type: 'number-group',
    source_q: () => q_liabilities,
    options: (a) => [...(a.q_liabilities || [])].sort((x, y) => {
      const classOf = (label) => q_liabilities.input.options.find(o => o.label === label)?.class
      return LIABILITY_CLASSES.indexOf(classOf(x)) - LIABILITY_CLASSES.indexOf(classOf(y))
    })
  },
  next_q: q_liability_repaid
}

// Breather explaining liability categories, shown after selection.
export const q_liability_categories = {
  id: 'q_liability_categories',
  section: 'Pocketwatch tip',
  step: 12,
  question: 'Liabilities have three classes —\nMortgages, Long Term Debts and Short Term Debts.\n\nThey help PocketWatch forcast your net worth.',
  note: '.',
  next_q: q_liability_valuations
}

export const q_liabilities = {
  id: 'q_liabilities',
  section: 'Your Liabilities',
  step: 11,
  question: 'Which of these do you currently have?',
  note: 'Not listed below? You\'ll have a chance to add it later.',
  input: {
    type: 'checkbox',
    options: [
      { label: 'Mortgage', class: 'Mortgage' },
      { label: 'Student loan', class: 'Long Term Debt' },
      { label: 'Credit card', class: 'Short Term Debt' },
      { label: 'Car finance', class: 'Long Term Debt' },
      { label: 'Personal loan', class: 'Long Term Debt' },
      { label: 'Joint loan', class: 'Long Term Debt' },
    ]
  },
  next_q: q_liability_categories
}

// Breather before the liabilities questions.
export const q_liabilities_intro = {
  id: 'q_liabilities_intro',
  section: 'Your Portfolio',
  step: 10,
  question: 'Great progress!\nNow on to what you owe.',
  next_q: q_liabilities
}

// ─── Assets ───

export const q_valuations = {
  id: 'q_valuations',
  section: 'Your Assets',
  step: 9,
  question: 'What are your assets worth?',
  input: {
    type: 'number-group',
    source_q: () => q_assets,
    options: (a) => [...(a.q_assets || [])].sort((x, y) => {
      const classOf = (label) => q_assets.input.options.find(o => o.label === label)?.class
      return ASSET_CLASSES.indexOf(classOf(x)) - ASSET_CLASSES.indexOf(classOf(y))
    })
  },
  next_q: q_liabilities_intro
}

// Breather explaining asset categories, shown after selection.
export const q_asset_categories = {
  id: 'q_asset_categories',
  section: 'Pocketwatch tip',
  step: 8,
  question: 'Every asset belongs to a class — \nProperty, Pensions, Investments, Savings, or Other.',
  note: 'Pocketwatch groups your assets so you can compare different aspects — like property vs. savings.\n\nYou can change the class anytime in your asset\'s settings.',
  next_q: q_valuations
}

export const q_assets = {
  id: 'q_assets',
  section: 'Your assets',
  step: 7,
  question: 'Which of these assets do you currently own?',
  note: 'Don\'t worry if your asset isn\'t shown, you\'ll have a chance to add it later.',
  input: {
    type: 'checkbox',
    options: [
      { label: 'House', class: 'Property' },
      { label: 'Flat', class: 'Property' },
      { label: 'Buy to let', class: 'Property' },
      { label: 'Work pension', class: 'Pension' },
      { label: 'Personal pension', class: 'Pension' },
      { label: 'Cash ISA', class: 'Investment' },
      { label: 'Stocks and shares ISA', class: 'Investment' },
      { label: 'LISA', class: 'Investment' },
      { label: 'Term deposit', class: 'Savings' },
      { label: 'Premium bonds', class: 'Savings' },
      { label: 'Savings', class: 'Savings' },
      { label: 'Cash', class: 'Savings' },
      { label: 'Crypto', class: 'Other' },
      { label: 'Gold', class: 'Other' },

    ]
  },
  next_q: q_asset_categories
}

// Breather before the portfolio questions.
export const q_assets_intro = {
  id: 'q_assets_intro',
  section: 'Your portfolio',
  step: 6,
  question: 'Now, let’s build your portfolio.\nFirst up — the things you own.',
  next_q: q_assets
}

// ─── Location side quest (non-UK users) ───

export const q_thanks = {
  id: 'q_thanks',
  section: 'About you',
  step: 5,
  question: 'Thanks!\nWe’ll let you know when PocketWatch launches near you.',
  end: true,
  next_q: null,
}

export const q_uk_only = {
  id: 'q_uk_only',
  section: 'About you',
  step: 5,
  question: 'PocketWatch is currently UK-only.\nWould you like to continue anyway?',
  input: { type: 'radio', options: ['Continue anyway', 'Stop for now'] },
  next_q: (value) => value === 'Continue anyway' ? q_assets_intro : q_thanks,
}

export const q_country = {
  id: 'q_country',
  section: 'About you',
  step: 5,
  question: 'Which country are you based in?',
  input: { type: 'select', options: COUNTRIES, placeholder: 'Choose your country' },
  onboarding_key: 'country',
  next_q: q_uk_only,
}

export const q_location = {
  id: 'q_location',
  section: 'About you',
  step: 5,
  question: 'Nice. And where are you based?',
  input: { type: 'radio', options: ['UK', 'Elsewhere in the world'] },
  onboarding_key: 'location',
  next_q: (value) => value === 'Elsewhere in the world' ? q_country : q_assets_intro
}

export const q_joint = {
  id: 'q_joint',
  section: 'About you',
  step: 4,
  question: 'Will you track your wealth individually or with your partner?',
  input: { type: 'radio', options: ['Individual', 'With my partner'] },
  next_q: q_location
}

export const q_generation = {
  id: 'q_generation',
  section: 'About you',
  step: 3,
  question: (a) => `Hi${a.q_display_name ? ' ' + a.q_display_name : ''}, what stage of the wealth journey are you on?`,
  input: { type: 'radio', options: ['Creating wealth (13-28)', 'Building wealth (29-44)', 'Maximising wealth (44-59)', 'Managing wealth (60+)'] },
  onboarding_key: 'generation',
  next_q: q_joint
}

export const q_display_name = {
  id: 'q_display_name',
  section: 'About you',
  step: 2,
  question: 'Let’s set up your account.\nFirstly, what should we call you?',
  input: { type: 'text', placeholder: 'Your name' },
  onboarding_key: 'display_name',
  next_q: q_generation,
}

// Breather/explainer screen — no input, just Next.
export const q_welcome = {
  id: 'q_welcome',
  section: 'Hello',
  step: 1,
  question: 'Welcome to PocketWatch \n Let’s build a picture of your wealth.',
  note: 'A few quick questions and you’ll be up and running.',
  button: 'Let’s Go',
  next_q: q_display_name,
}

// The first question in the chain.
export const FIRST_QUESTION = q_welcome

// Total main-flow steps for the progress bar. Side-quest screens share their
// parent's step, so the bar holds steady during a detour.
export const TOTAL_STEPS = q_thanks_final.step
